/* Prompt attachments — let the user upload files alongside the prompt.
 *
 * Supported in v1:
 *   - Plain text formats (txt, md, json, csv, yaml, xml, html, code) → read
 *     as UTF-8, prepended to the prompt with marker headers. Works for
 *     every model.
 *   - Word .docx + PowerPoint .pptx → extracted to text via JSZip + the
 *     OOXML <w:t> / <a:t> tags. Treated as text from there.
 *   - PDF → kept as base64. Models with native PDF support (Anthropic +
 *     Gemini, per data/models.ts) receive it natively as a document part;
 *     others see a short note ("[PDF attached but not supported by this
 *     model]") so the run still completes.
 *   - Images (png/jpg/webp/gif) → kept as base64. Vision-capable models
 *     receive them as image parts; non-vision models see a note.
 *
 * Storage: bytes live in state.promptAttachments as base64 (or extracted
 * text). The snapshot serializer copies the array verbatim into the
 * cloud blob / local JSON file so reloading the session restores both
 * the chips and the bytes — load-then-rerun produces the same output.
 *
 * UI: file picker + drag-and-drop on the prompt textarea. Each pending
 * attachment renders as a chip above the textarea with kind icon + name
 * + size + ✕ to remove. attachAttachmentsHandlers() wires the button +
 * drop zone in the prompt panel.
 */

import { state } from './state';
import { toast } from './toast';
import { t } from './i18n';
import { escapeHtml } from './safe-dom';

// 10 MB per attachment — fits comfortably in a cloud blob and in the
// snapshot JSON without bloating browsers' fetch bodies.
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

// Extensions the runner can read as UTF-8 text without any extraction step.
const TEXT_EXTS = new Set([
  'txt', 'md', 'markdown', 'mdx', 'rst', 'org',
  'json', 'jsonl', 'ndjson', 'yaml', 'yml', 'toml', 'ini', 'env',
  'csv', 'tsv',
  'xml', 'html', 'htm', 'svg',
  'js', 'mjs', 'cjs', 'ts', 'tsx', 'jsx',
  'py', 'rb', 'go', 'rs', 'java', 'kt', 'kts', 'swift', 'cs', 'cpp', 'c', 'h', 'hpp',
  'sh', 'bash', 'zsh', 'ps1', 'bat', 'sql', 'graphql', 'gql',
  'log', 'conf', 'cfg', 'tf', 'tfvars',
]);
const IMAGE_MIMES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const PDF_MIMES = new Set(['application/pdf']);
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

function randomId(): string {
  // Short opaque id; enough entropy for chip-tracking within one session.
  const buf = new Uint8Array(8);
  (globalThis.crypto || (globalThis as any).msCrypto).getRandomValues(buf);
  return Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// ============================================================================
// File → Attachment extraction
// ============================================================================

async function readAsText(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(r.error);
    r.readAsText(f, 'utf-8');
  });
}

async function readAsBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      // result is "data:<mime>;base64,<b64>" — strip the prefix
      const s = String(r.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(f);
  });
}

/* Walk every paragraph in an OOXML doc and join its text runs, adding a
 * newline per paragraph (including empty ones for visual spacing). Works
 * across Chromium / Firefox / Safari — earlier version used
 * Element.closest('p') which is namespace-blind in non-Chromium browsers
 * and could match nothing, flattening the whole doc to one line. */
function joinParagraphs(doc: Document, ns: string): string {
  const paras = doc.getElementsByTagNameNS(ns, 'p');
  let buf = '';
  for (let i = 0; i < paras.length; i++) {
    const runs = paras[i].getElementsByTagNameNS(ns, 't');
    let para = '';
    for (let j = 0; j < runs.length; j++) {
      para += (runs[j].textContent || '');
    }
    buf += para + '\n';
  }
  return buf.replace(/\n{3,}/g, '\n\n').trim();
}

/* Read a .docx → join every <w:p> paragraph's <w:t> text runs.
 * Exported under a _-prefix so the test file can call it directly without
 * having to drive the whole file-picker pipeline. Not part of the public
 * surface — don't import from feature code. */
export async function _extractDocxText(f: File): Promise<string> {
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(await f.arrayBuffer());
  const docXml = await zip.file('word/document.xml')?.async('string');
  if (!docXml) return '';
  const doc = new DOMParser().parseFromString(docXml, 'application/xml');
  const NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  return joinParagraphs(doc, NS);
}

/* Read a .pptx → walk every slide's <a:p> paragraphs in order. Adds slide
 * separators so the LLM can tell where one ends and the next starts.
 * Includes notes slides (speaker notes) when present so the LLM gets the
 * full picture. */
export async function _extractPptxText(f: File): Promise<string> {
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(await f.arrayBuffer());
  const slideFiles = Object.keys(zip.files)
    .filter(n => /^ppt\/slides\/slide\d+\.xml$/.test(n))
    .sort((a, b) => {
      const ai = parseInt(a.match(/slide(\d+)/)![1], 10);
      const bi = parseInt(b.match(/slide(\d+)/)![1], 10);
      return ai - bi;
    });
  const NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
  let buf = '';
  for (let s = 0; s < slideFiles.length; s++) {
    const xml = await zip.file(slideFiles[s])!.async('string');
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (s > 0) buf += '\n\n';
    buf += `--- Slide ${s + 1} ---\n`;
    buf += joinParagraphs(doc, NS);
    // Speaker notes for this slide (best-effort — pptx links them by id
    // but the convention is slideN.xml → notesSlideN.xml).
    const slideIdx = slideFiles[s].match(/slide(\d+)/)![1];
    const notesPath = `ppt/notesSlides/notesSlide${slideIdx}.xml`;
    const notesFile = zip.file(notesPath);
    if (notesFile) {
      const notesXml = await notesFile.async('string');
      const notesDoc = new DOMParser().parseFromString(notesXml, 'application/xml');
      const notesText = joinParagraphs(notesDoc, NS).trim();
      if (notesText) buf += `\n(Speaker notes)\n${notesText}`;
    }
  }
  return buf.trim();
}

interface PreparedAttachment {
  id: string; name: string; mime: string; size: number;
  kind: 'text' | 'image' | 'pdf' | 'binary';
  text?: string; base64?: string; sourceMime?: string;
}

async function fileToAttachment(f: File): Promise<PreparedAttachment | null> {
  if (f.size > MAX_SIZE_BYTES) {
    toast(t('attach.too_big', { name: f.name, max: '10 MB' }) ||
      `${f.name} is bigger than 10 MB — skipped.`);
    return null;
  }
  const ext = extOf(f.name);
  const mime = f.type || '';
  const id = randomId();
  const base = { id, name: f.name, mime, size: f.size };

  // 1) Native text formats — read as UTF-8.
  if (TEXT_EXTS.has(ext) || mime.startsWith('text/') || mime === 'application/json') {
    return { ...base, kind: 'text', text: await readAsText(f) };
  }
  // 2) DOCX → extracted text (sourceMime preserves the docx label for the chip).
  if (mime === DOCX_MIME || ext === 'docx') {
    try {
      const text = await _extractDocxText(f);
      return { ...base, kind: 'text', text, sourceMime: DOCX_MIME };
    } catch (err) {
      console.warn('docx extract failed', err);
      toast(t('attach.docx_failed', { name: f.name }) || `Could not read ${f.name}.`);
      return null;
    }
  }
  // 3) PPTX → extracted text.
  if (mime === PPTX_MIME || ext === 'pptx') {
    try {
      const text = await _extractPptxText(f);
      return { ...base, kind: 'text', text, sourceMime: PPTX_MIME };
    } catch (err) {
      console.warn('pptx extract failed', err);
      toast(t('attach.pptx_failed', { name: f.name }) || `Could not read ${f.name}.`);
      return null;
    }
  }
  // 4) Images → base64.
  if (IMAGE_MIMES.has(mime) || ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) {
    return { ...base, kind: 'image', base64: await readAsBase64(f) };
  }
  // 5) PDFs → base64 (decision delegated to per-provider routing).
  if (PDF_MIMES.has(mime) || ext === 'pdf') {
    return { ...base, kind: 'pdf', base64: await readAsBase64(f) };
  }
  // 6) Anything else → kept as binary so it survives in the snapshot, but
  //    the dispatcher only surfaces a note to the model.
  return { ...base, kind: 'binary', base64: await readAsBase64(f) };
}

// ============================================================================
// State + UI
// ============================================================================

export function getAttachments(): PreparedAttachment[] {
  return (state as any).promptAttachments || [];
}

export function clearAttachments(): void {
  (state as any).promptAttachments = [];
  renderChips();
}

function removeAttachment(id: string): void {
  const arr: PreparedAttachment[] = (state as any).promptAttachments || [];
  (state as any).promptAttachments = arr.filter(a => a.id !== id);
  renderChips();
}

async function addFiles(files: FileList | File[]): Promise<void> {
  const list = Array.from(files);
  if (!list.length) return;
  const prepared = await Promise.all(list.map(fileToAttachment));
  const ok = prepared.filter(Boolean) as PreparedAttachment[];
  if (!ok.length) return;
  const cur: PreparedAttachment[] = (state as any).promptAttachments || [];
  (state as any).promptAttachments = [...cur, ...ok];
  renderChips();
  toast(t('attach.added', { count: ok.length }) || `Attached: ${ok.map(a => a.name).join(', ')}`);
}

function chipIcon(a: PreparedAttachment): string {
  if (a.kind === 'image') return '🖼';
  if (a.kind === 'pdf') return '📕';
  if (a.sourceMime === DOCX_MIME) return '📄';
  if (a.sourceMime === PPTX_MIME) return '📊';
  if (a.kind === 'binary') return '📦';
  return '📝';
}

function renderChips(): void {
  const host = document.getElementById('attach-chips');
  if (!host) return;
  const arr = getAttachments();
  if (!arr.length) { host.innerHTML = ''; host.style.display = 'none'; return; }
  host.style.display = '';
  const chipsHtml = arr.map(a => `
    <span class="attach-chip" data-id="${escapeHtml(a.id)}" title="${escapeHtml(a.mime || a.name)}">
      <span class="attach-chip-icon">${chipIcon(a)}</span>
      <span class="attach-chip-name">${escapeHtml(a.name)}</span>
      <span class="attach-chip-size">${fmtBytes(a.size)}</span>
      <button class="attach-chip-rm" type="button" data-id="${escapeHtml(a.id)}" aria-label="Remove">×</button>
    </span>
  `).join('');
  // Only show the clear-all link when there are 2+ chips — for a single
  // chip the per-chip ✕ is enough.
  const clearLink = arr.length >= 2
    ? `<button class="attach-clear-all" type="button" title="${escapeHtml(t('attach.clear_all') || 'Remove all attachments')}">${escapeHtml(t('attach.clear_all') || 'Clear all')}</button>`
    : '';
  host.innerHTML = chipsHtml + clearLink;
  host.querySelectorAll<HTMLButtonElement>('.attach-chip-rm').forEach(btn => {
    btn.addEventListener('click', () => removeAttachment(btn.dataset.id!));
  });
  host.querySelector<HTMLButtonElement>('.attach-clear-all')?.addEventListener('click', () => {
    clearAttachments();
  });
}

// Repaint chips after a session restore.
export function refreshChips(): void { renderChips(); }

// NB: the previous version of this module exported `applyAttachmentsToPrompt`
// to prepend text attachments to the prompt. It was orphaned (nothing called
// it) and dropped text attachments silently — the dispatcher in src/api/
// index.ts now owns that logic so per-model gating + text prepend live in
// the same place. Don't reintroduce a wrapper here without removing the
// dispatcher copy.

// ============================================================================
// Wiring
// ============================================================================

export function attachAttachmentsHandlers(): void {
  const btn = document.getElementById('attach-btn');
  const input = document.getElementById('attach-input') as HTMLInputElement | null;
  const drop = document.getElementById('prompt-input') as HTMLTextAreaElement | null;

  btn?.addEventListener('click', (e) => {
    e.preventDefault();
    input?.click();
  });
  input?.addEventListener('change', () => {
    if (input.files) addFiles(input.files);
    input.value = '';  // allow re-picking the same file later
  });

  if (drop) {
    const stop = (e: DragEvent) => { e.preventDefault(); e.stopPropagation(); };
    drop.addEventListener('dragenter', (e) => { stop(e); drop.classList.add('drop-target'); });
    drop.addEventListener('dragover',  (e) => { stop(e); drop.classList.add('drop-target'); });
    drop.addEventListener('dragleave', (e) => { stop(e); drop.classList.remove('drop-target'); });
    drop.addEventListener('drop', async (e) => {
      stop(e);
      drop.classList.remove('drop-target');
      const files = e.dataTransfer?.files;
      if (files && files.length) await addFiles(files);
    });
  }

  renderChips();
}
